/**
 * slide-stage-utils
 *
 * Pure geometry + the image-aspect hook behind the slide "stage" used by
 * both presentation windows (see SlideStage.tsx). Kept out of the
 * component file so it can be unit-tested directly and so the component
 * module only exports components (Fast Refresh friendly).
 */
import { useCallback, useState } from "react"

export interface Box {
  width: number
  height: number
}

/** Default slide aspect (width / height) until a real image loads. */
export const DEFAULT_SLIDE_ASPECT = 16 / 9

/**
 * Largest box of `aspect` (width / height) that fits inside
 * parentW × parentH, centered (letterboxed). Pure — exported for tests.
 */
export function containedBox(parentW: number, parentH: number, aspect: number): Box {
  if (parentW <= 0 || parentH <= 0 || !Number.isFinite(aspect) || aspect <= 0) {
    return { height: Math.max(0, parentH), width: Math.max(0, parentW) }
  }
  const parentAspect = parentW / parentH
  if (parentAspect > aspect) {
    // Parent is wider than the slide → full height, letterbox left/right.
    const height = parentH
    return { height, width: height * aspect }
  }
  // Parent is taller/narrower → full width, letterbox top/bottom.
  const width = parentW
  return { height: width / aspect, width }
}

/**
 * Tracks a slide image's aspect ratio. Wire `onImgLoad` to the slide
 * `<img>`'s `onLoad`; it reads `naturalWidth/Height` so the stage can be
 * shaped to the real slide (16:9, 4:3, …) instead of a guess. Defaults to
 * 16:9 until the first image loads.
 */
export function useImageAspect(defaultAspect = DEFAULT_SLIDE_ASPECT): {
  aspect: number
  onImgLoad: (e: React.SyntheticEvent<HTMLImageElement>) => void
} {
  const [aspect, setAspect] = useState(defaultAspect)
  const onImgLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget
    if (img.naturalWidth > 0 && img.naturalHeight > 0) {
      const next = img.naturalWidth / img.naturalHeight
      setAspect((prev) => (Math.abs(prev - next) < 0.0001 ? prev : next))
    }
  }, [])
  return { aspect, onImgLoad }
}
